import { SESSION_STATES } from '../constants';

/**
 * Creates normalized session data object from raw session input.
 * Validates session has required ID and converts date strings to Date objects.
 *
 * @param {ChatSession} session - Raw session object from API
 * @returns {ChatSessionNormalized} Normalized session data with title and converted dates
 * @throws {Error} Throws error if session lacks required ID
 */
const createSessionData = (session) => {
  if (!session?.id) {
    throw new Error('Session must have an ID');
  }

  return {
    id: session.id,
    title: session.title || SESSION_STATES.NEW_CHAT_TITLE,
    displayTitle: session.displayTitle || SESSION_STATES.NEW_CHAT_TITLE,
    createdAt: new Date(session.createdAt),
    updatedAt: new Date(session.updatedAt),
  };
};

/**
 * Normalizes an array of session objects into a key-value object indexed by session ID.
 * Invalid sessions are filtered out with warning logs.
 *
 * @param {Array<ChatSession>} sessions - Array of session objects to normalize
 * @returns {Object<string, ChatSessionNormalized>} Normalized sessions object with session IDs as keys
 * @returns {Object} Returns empty object if sessions is not an array
 */
const normalizeSessions = (sessions) => {
  if (!Array.isArray(sessions)) {
    return {};
  }

  return sessions.reduce((normalized, session) => {
    try {
      const sessionData = createSessionData(session);
      normalized[session.id] = sessionData;
    } catch (error) {
      console.warn('Failed to normalize session:', session.id, error);
    }
    return normalized;
  }, {});
};

const generateTitleFromMessage = (message) => {
  const content = message.content || '';
  const maxLength = 50;

  return content.length > maxLength ? content.slice(0, maxLength) + '...' : content;
};

/**
 * Sorts an array of sessions by their updatedAt date.
 *
 * @param {Array<ChatSessionNormalized | ChatSession>} sessions - Array of session objects to sort
 * @param {string} [order='desc'] - Sort order: 'desc' for newest first, 'asc' for oldest first
 * @returns {Array<ChatSessionNormalized | ChatSession>} Array of sessions sorted by updatedAt date
 */
const sortSessionsArrayByDate = (sessions, order = 'desc') => {
  return sessions.toSorted((a, b) => {
    const dateA = new Date(a.updatedAt);
    const dateB = new Date(b.updatedAt);
    return order === 'desc' ? dateB - dateA : dateA - dateB;
  });
};

/**
 * Sorts normalized sessions by their updatedAt date.
 *
 * @param {ChatSessionNormalized | ChatSession} sessions - Object of normalized sessions indexed by ID
 * @param {string} [order='desc'] - Sort order: 'desc' for newest first, 'asc' for oldest first
 * @returns {Array<ChatSessionNormalized>} Array of sessions sorted by updatedAt date
 */
const sortSessionsByDate = (sessions, order = 'desc') => {
  const sessionsArray = Object.values(sessions);
  return sortSessionsArrayByDate(sessionsArray, order);
};

/**
 * Finds the most recently updated session from a collection of normalized sessions.
 *
 * @param {Object<string, ChatSessionNormalized>} sessions - Object of normalized sessions indexed by ID
 * @returns {ChatSessionNormalized|null} The most recently updated session, or null if no sessions exist
 */
const findMostRecentSession = (sessions) => {
  const sortedSessions = sortSessionsByDate(sessions, 'desc');
  return sortedSessions.length > 0 ? sortedSessions[0] : null;
};

export { findMostRecentSession, generateTitleFromMessage, normalizeSessions };
